import { TestType } from '@/models/SharedTypes';
import type { RetakeReport } from '@/models/UserTypes';
import { UserSegment, AcquisitionChannel, UTMParams } from '@/types/user';

/**
 * Bridges trial-signup context across a `signInWithRedirect` round-trip.
 *
 * Redirect-based social auth navigates the page away to the provider and back,
 * destroying all in-memory React state. Before redirecting we stash the signup
 * context (consent, exam type, attribution) here; on return we rehydrate it to
 * finish creating the trial. sessionStorage (not localStorage) so it dies with
 * the tab and never leaks into a later session.
 */
const KEY = 'swtpa_pending_social_signup';

export interface PendingSocialSignup {
  provider: 'google' | 'apple';
  marketingConsent: boolean;
  examType?: TestType;
  userSegment?: UserSegment;
  acquisitionChannel?: AcquisitionChannel;
  utmParams?: UTMParams;
  /** ASWB school code from a /programs CTA, same reason retakeReport is here. */
  schoolId?: string;
  /**
   * Path to restore after `signInWithRedirect` when the auth handler lands on `/`.
   * Keeps a /try Keep-it round-trip from silently switching the URL to /.
   */
  returnPath?: string;
  isRetaker?: boolean;
  /**
   * The prior-attempt report gathered on the screen before the account step.
   * Carried here because a social REDIRECT is a full page load: the onboarding
   * component state that produced it is gone by the time we provision, so
   * without this the report would survive the popup path and vanish on the
   * redirect path (which is the path mobile always takes).
   */
  retakeReport?: RetakeReport;
}

export function savePendingSocialSignup(ctx: PendingSocialSignup): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(ctx));
  } catch {
    /* sessionStorage unavailable (private mode / blocked) — best effort */
  }
}

export function loadPendingSocialSignup(): PendingSocialSignup | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSocialSignup;
    if (parsed?.provider !== 'google' && parsed?.provider !== 'apple') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingSocialSignup(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* best effort */
  }
}
